import { getQRI } from '@qualio/search-qri';
import { useCurrentUser, useToastProvider } from '@qualio/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { changeControlApi } from '../../../../../../../api/changeControl';
import { documentApi } from '../../../../../../../api/document';
import {
  DocumentStatus,
  QualioDocument,
} from '../../../../../../../api/model/document';
import { ChangeControlTemplateDTO } from '../../../../../../../types/DocumentCreate';
import {
  useEmbeddedDocuments,
  useInvalidateEmbeddedDocuments,
} from '../../../../../DocumentOverview/hooks/useEmbeddedDocuments';
import { EmbedWarningModal } from '../../../../../DocumentOverview/Modals/EmbedWarningModal';
import { isIssueRelated } from '../../../../../DocumentOverview/RenderLogic';
import { WorkspaceTabs } from '../../../WorkspaceV2';
import {
  hasAllowedStatusesForAction,
  hasRequiredPermissionsForAction,
} from '../../BulkActions';
import { BulkActionButton } from '../BulkActionButton';
import { IsActionAllowedCallback } from '../ChangeOwnerButton/types';
import { CreateNewDraftProps } from './types';
import { useQriServiceUrl } from '../../../../../../../hooks/useQriServiceUrl';

export const CreateNewDraftButton = (props: CreateNewDraftProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const qriServiceUrl = useQriServiceUrl();

  const [isLoading, setIsLoading] = useState(false);
  const [isEmbedWarningModalOpen, setIsEmbedWarningModalOpen] = useState(false);

  const handleCreateNewDraft = async () => {
    setIsLoading(true);
    let newDraft: QualioDocument;
    let ccTab = '';
    try {
      newDraft = await documentApi.createDraftFromEffectiveDoc(
        currentUser.companyId,
        // the button will only show if there is only one document selected
        props.selectedDocuments[0].document_matrix_id,
      );
    } catch (e: any) {
      const failureMessage =
        e.response?.data?.message ||
        t('documentActions.failedToCreateNewDraft');
      showToast({
        'data-cy': 'bulk-v2-create-new-draft-error-toast',
        id: 'create-new-draft-error-toast',
        status: 'error',
        title: t('notifications.error'),
        description: failureMessage,
      });
      setIsLoading(false);
      return;
    }

    try {
      const changeControlTemplates: ChangeControlTemplateDTO[] =
        (await changeControlApi.fetchChangeControlTemplates()) ?? [];
      const changeControlTemplate = changeControlTemplates.find(
        (tmpl) => tmpl.qualio_managed && tmpl.status === 'effective',
      );
      if (changeControlTemplate) {
        await changeControlApi.createChangeControl(
          newDraft,
          Number(changeControlTemplate.id),
          { sections: changeControlTemplate.sections },
        );
      }
    } catch (e: any) {
      ccTab = '&tab=change-control';
    }

    void navigate(`/workspace/documents/${newDraft.id}?intent=edit${ccTab}`);
  };

  const canCreateNewDraft: IsActionAllowedCallback = (
    user,
    statuses,
    activeTab,
    requiredPermissions,
    selectedDocuments,
  ) => {
    if (
      !selectedDocuments.length ||
      selectedDocuments.length > 1 ||
      !hasRequiredPermissionsForAction(requiredPermissions, user.permissions) ||
      isIssueRelated(selectedDocuments[0])
    ) {
      return false;
    }

    if (activeTab === WorkspaceTabs.YOUR_DOCUMENTS) {
      return hasAllowedStatusesForAction(
        [
          DocumentStatus.Approved,
          DocumentStatus.Effective,
          DocumentStatus.For_Retirement,
          DocumentStatus.Retired,
          DocumentStatus.For_Periodic_Review,
        ],
        statuses,
      );
    }
    return hasAllowedStatusesForAction(
      [
        DocumentStatus.Approved,
        DocumentStatus.Effective,
        DocumentStatus.For_Periodic_Review,
      ],
      statuses,
    );
  };

  const documentQri = getQRI({
    companyId: currentUser.companyId.toString(),
    domain: 'documents',
    domainType: 'document',
    id: props.selectedDocuments[0]?.document_matrix_id ?? '',
    qriBaseUrl: qriServiceUrl,
  });

  const shouldFetchEmbeddedDocuments =
    !!props.selectedDocuments[0] &&
    props.selectedDocuments.length === 1 &&
    props.statuses.every((s) => s === DocumentStatus.Effective);

  const invalidateEmbeddedDocuments =
    useInvalidateEmbeddedDocuments(documentQri);
  const { data, isLoading: isEmbeddedDocumentsLoading } = useEmbeddedDocuments(
    documentQri,
    shouldFetchEmbeddedDocuments,
    currentUser.companyId,
  );
  const embeddedDocuments = data ?? [];

  const handleButtonClick = async () => {
    await invalidateEmbeddedDocuments();
    if (embeddedDocuments.length > 0) {
      setIsEmbedWarningModalOpen(true);
    } else {
      await handleCreateNewDraft();
    }
  };

  return (
    <>
      <BulkActionButton
        {...props}
        isActionAllowed={canCreateNewDraft}
        onClick={handleButtonClick}
        isLoading={isLoading || isEmbeddedDocumentsLoading}
      />
      <EmbedWarningModal
        isOpen={isEmbedWarningModalOpen}
        handleCreate={handleCreateNewDraft}
        handleClose={() => setIsEmbedWarningModalOpen(false)}
        embeddedDocuments={embeddedDocuments}
      />
    </>
  );
};
